import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CommentOutlined } from '@mui/icons-material';
import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, isRenzokuMove, isShellSideArmMove } from '../../move/move';
import { retCalc } from '../../types/damage';

interface DamageResultDetailsProps {
  move: Move;
  pokemonId: number;
  damageText: string;
  damageDetail?: retCalc | null;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;
  customLabel?: string;
  hideMoveMeta?: boolean;
}

function DamageResultDetails({
  move,
  pokemonId,
  damageText,
  damageDetail,
  getMoveDisplayName,
  getMoveDisplayPower,
  customLabel,
  hideMoveMeta,
}: DamageResultDetailsProps) {
  const [openModal, setOpenModal] = useState(false);
  const headerLabel = customLabel ?? getMoveDisplayName(move, pokemonId);
  const showMoveMeta = !hideMoveMeta;

  const renderDamageContent = () => {
    if (isShellSideArmMove(move) && damageDetail?.shell_side_arm_details) {
      const details = damageDetail.shell_side_arm_details;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'primary.main' }}>
            {details.physical_damage.text}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'secondary.main' }}>
            {details.special_damage.text}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 'bold' }}>
            → {details.selected_type === 'physical' ? '物理' : '特殊'}を採用: {damageText}
          </Typography>
        </Box>
      );
    }

    if (isRenzokuMove(move)) {
      const allLines = damageText.split('\n');
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* 連続技は改行ごとに分割して見やすく表示する */}
          {allLines.map((line, index) =>
            line ? (
              <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {line}
              </Typography>
            ) : null,
          )}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
        {damageText}
      </Typography>
    );
  };

  const renderRandomTable = () => {
    if (!damageDetail || !damageDetail.random_table || Object.keys(damageDetail.random_table).length === 0) {
      return (
        <Typography variant="body2" sx={{ mb: 1 }}>
          ダメージが計算できませんでした。
        </Typography>
      );
    }

    const randomTable = damageDetail.random_table;
    const damagePatterns: { pattern: number; damage: number }[] = [];
    let patternCount = 1;
    const sortedDamages = Object.keys(randomTable)
      .map((d) => parseInt(d, 10))
      .sort((a, b) => a - b);

    for (const damage of sortedDamages) {
      const count = randomTable[damage];
      for (let i = 0; i < count; i++) {
        // 乱数分布を「パターン番号 → ダメージ」に展開して表の列に使う
        damagePatterns.push({ pattern: patternCount, damage });
        patternCount += 1;
      }
    }

    return (
      <Box>
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>パターン</TableCell>
                {damagePatterns.map(({ pattern }) => (
                  <TableCell key={pattern} align="center">
                    {pattern}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                  ダメージ
                </TableCell>
                {damagePatterns.map(({ pattern, damage }) => (
                  <TableCell key={pattern} align="center">
                    {damage}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
          {headerLabel}
          {showMoveMeta && (
            <>
              (威力: {getMoveDisplayPower(move, pokemonId)},
              {isShellSideArmMove(move)
                ? '物理/特殊選択'
                : move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL
                ? '物理'
                : '特殊'}
              )
            </>
          )}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setOpenModal(true)}
          sx={{ padding: '2px', minWidth: 'auto', '& .MuiSvgIcon-root': { fontSize: '0.9rem' } }}
        >
          <CommentOutlined />
        </IconButton>
      </Box>
      {renderDamageContent()}

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="damage-detail-modal"
        aria-describedby="damage-detail-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'auto',
            minWidth: 600,
            maxWidth: '95vw',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="damage-detail-modal" variant="h6" component="h2" sx={{ mb: 2 }}>
            乱数ダメージ詳細
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {headerLabel}
          </Typography>
          {renderRandomTable()}
        </Box>
      </Modal>
    </>
  );
}

export default DamageResultDetails;
